import "./TemplateSelectionPage.scss";

import {
  Button,
  Card,
  CardBody,
  CardHeader,
  ChevronStartIcon,
  Divider,
  Flex,
  FlexItem,
  Text,
} from "@fluentui/react-northstar";
import { DialogDimension, dialog } from "@microsoft/teams-js";
import { TeamsQuestionnaireTemplateCategory } from "@teams/types";
import { QuestionnaireTemplateDTO } from "@teams/types/api";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router";

import { useUserInfo } from "../../_shared/actions/useUserInfo";
import { ResponseUsageMeter } from "../../_shared/components/MeterBars/ResponseUsageMeter";
import { PollyIcon, PollyIconName } from "../../_shared/components/PollyIcon";
import { TeamsLoader } from "../../_shared/components/TeamsLoader";
import { TemplatesEmptyStateComponent } from "../../_shared/components/TemplatesEmptyStateComponent";
import { useTeamsContext } from "../../_shared/state/TeamsContextProvider";
import { isMobileClient } from "../../_shared/utils/clientHelper";
import { getAuthoringPath } from "../../_shared/utils/getAuthoringPath";
import useWindowDimensions from "../../_shared/utils/useWindowDimensions";
import { useTemplates } from "../actions/useTemplates";
import { PollyPreview } from "./SurveyAuthoring/PollyPreview";
import { TemplateManagementMenu } from "./SurveyAuthoring/Templates/TemplateManagementMenu";

export const TemplateSelection: React.FC = () => {
  const hidePreviewOnSize = 1000;

  const history = useHistory();
  const location = useLocation();
  const { width } = useWindowDimensions();

  const { teamsContext } = useTeamsContext();
  const { user } = useUserInfo();
  const [templateScope, setTemplateScope] = useState<"global" | "personal">("personal");
  const { templates, isTemplatesLoading, loadTemplates } = useTemplates();
  const [filteredCategory, setFilteredCategory] = useState<string | null>(null);
  const [selectedTemplateIndex, setSelectedTemplateIndex] = useState<number>(0);

  const redirectedFromSuccess = useMemo(() => {
    const urlParms = new URLSearchParams(location.search);
    return urlParms.get("fromSuccessPage") === "true";
  }, [location.search]);

  useEffect(() => {
    dialog.update.resize({ width: 1400, height: DialogDimension.Large });

    if (redirectedFromSuccess) {
      setTemplateScope("global");
      return;
    }
    setTemplateScope("personal");
  }, [redirectedFromSuccess]);

  useEffect(() => {
    if (!templateScope) {
      return;
    }
    loadTemplates(teamsContext, templateScope);
  }, [templateScope, teamsContext, loadTemplates]);

  const navigateToTemplate = useCallback(
    (template: QuestionnaireTemplateDTO) => {
      const navTo = getAuthoringPath(template.type, location, {
        templateId: template.id,
        trackingSource: templateScope === "global" ? "templates-global" : "templates-personal",
      });

      history.push(navTo);
    },
    [history, location, templateScope]
  );

  const templateCategories = useMemo(() => {
    const categories = new Set<string>();

    switch (templateScope) {
      case "global":
        templates.forEach(template => {
          template.categories?.forEach(category => categories.add(category));
        });
        break;
      case "personal":
        categories.add("Shared with me");
        break;
    }

    return Array.from(categories);
  }, [templates, templateScope]);

  const filteredTemplates = useMemo(() => {
    const sortedTemplates = templates.sort((a, b) => (a.templateName > b.templateName ? 1 : -1));

    if (!filteredCategory) {
      return sortedTemplates;
    }

    switch (templateScope) {
      case "global":
        return sortedTemplates.filter(template =>
          template.categories?.includes(filteredCategory as TeamsQuestionnaireTemplateCategory)
        );
      case "personal":
        return sortedTemplates.filter(
          template => !template.owners?.some(owner => owner.aadObjectId === user.userId)
        );
    }
  }, [templates, filteredCategory, templateScope, user.userId]);

  const previewTemplate = filteredTemplates[selectedTemplateIndex];

  return (
    <div className="template-selection">
      <Flex column gap="gap.small">
        <TemplateSelectActionBar />

        <Flex gap="gap.small">
          <Flex column gap="gap.small" className="template-selection_grow">
            <Flex gap="gap.large">
              <TemplateSelectButton
                label="My Templates"
                icon="templates_2"
                selected={templateScope === "personal"}
                onSelect={() => {
                  setTemplateScope("personal");
                  setFilteredCategory(null);
                  setSelectedTemplateIndex(0);
                }}
              />

              <TemplateSelectButton
                label="Global Templates"
                icon="templates-icon"
                selected={templateScope === "global"}
                onSelect={() => {
                  setTemplateScope("global");
                  setFilteredCategory(null);
                  setSelectedTemplateIndex(0);
                }}
              />
            </Flex>

            <TeamsLoader isLoading={isTemplatesLoading} loadingMessage="Loading templates...">
              <Flex gap="gap.medium">
                <CategoryFilter
                  filteredCategory={filteredCategory}
                  templateCategories={templateCategories}
                  setFilter={(category: string | null) => {
                    setSelectedTemplateIndex(0);
                    setFilteredCategory(category);
                  }}
                />
                <Flex className="template-selection_card-container template-selection_grow" column>
                  <TemplateCards
                    selectedIndex={selectedTemplateIndex}
                    templates={filteredTemplates}
                    loadTemplates={() => {
                      loadTemplates(teamsContext, "personal");
                    }}
                    onClickTemplate={(index: number) => {
                      setSelectedTemplateIndex(index);
                    }}
                  />

                  <div className="template-selection_card-divider"></div>
                  <FlexItem align="end">
                    <Button
                      primary
                      content="Use this Template"
                      disabled={!previewTemplate}
                      style={{ marginRight: "20px", marginBottom: "16px" }}
                      onClick={() => {
                        navigateToTemplate(previewTemplate);
                      }}
                    />
                  </FlexItem>
                </Flex>
              </Flex>
            </TeamsLoader>
          </Flex>

          {previewTemplate && width > hidePreviewOnSize && !isTemplatesLoading && (
            <div className="template-selection_preview">
              <>
                <div style={{ paddingBottom: "15px" }}>
                  <Divider content="Preview" />
                </div>
                <PollyPreview survey={previewTemplate} />
              </>
            </div>
          )}
        </Flex>
      </Flex>
    </div>
  );
};

const TemplateSelectActionBar = () => {
  const history = useHistory();
  const location = useLocation();
  return (
    <Flex className="template-selection-info-bar">
      <Flex.Item grow>
        <div className="polly-authoring-page__back-button">
          <Button
            text
            content={
              <Flex vAlign="center">
                <ChevronStartIcon xSpacing="after" />
                Back
              </Flex>
            }
            onClick={() => {
              // If location.key is empty that means the history stack is empty and cannot be navigated to the previous page
              if (location.key) {
                history.goBack();
              } else {
                dialog.url.submit();
              }
            }}
          />
        </div>
      </Flex.Item>
      <Flex.Item push>
        <ResponseUsageMeter />
      </Flex.Item>
    </Flex>
  );
};

const TemplateSelectButton = ({
  label,
  icon,
  selected,
  onSelect,
}: {
  label: string;
  icon: PollyIconName;
  selected: boolean;
  onSelect: () => void;
}) => {
  const classes = ["template-select-button"];
  if (selected) {
    classes.push("template-select-button--selected");
  }

  return (
    <Flex
      vAlign="center"
      className={classes.join(" ")}
      gap="gap.smaller"
      onClick={() => {
        onSelect();
      }}
    >
      <PollyIcon icon={icon} inheritColor />
      <div>{label}</div>
    </Flex>
  );
};

const CategoryButton = ({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) => {
  const classes = ["category-select-button"];
  if (selected) {
    classes.push("category-select-button--selected");
  }

  return (
    <div
      className={classes.join(" ")}
      onClick={() => {
        onClick();
      }}
    >
      {label}
    </div>
  );
};

const CategoryFilter = ({
  filteredCategory,
  templateCategories,
  setFilter,
}: {
  filteredCategory: string | null;
  templateCategories: string[];
  setFilter: (category: string | null) => void;
}) => {
  return (
    <Flex column>
      <CategoryButton
        key="All"
        label="All"
        selected={filteredCategory === null}
        onClick={() => setFilter(null)}
      />

      {templateCategories.map(category => {
        return (
          <CategoryButton
            key={category}
            label={category}
            selected={filteredCategory === category}
            onClick={() => setFilter(category)}
          />
        );
      })}
    </Flex>
  );
};

const TemplateCards = ({
  templates,
  selectedIndex,
  loadTemplates,
  onClickTemplate,
}: {
  templates: QuestionnaireTemplateDTO[];
  selectedIndex: number;
  loadTemplates: () => void;
  onClickTemplate: (index: number, template: QuestionnaireTemplateDTO) => void;
}) => {
  return (
    <div className="template-selection_card-list">
      {templates.length === 0 ? (
        <TemplatesEmptyStateComponent />
      ) : (
        <TemplateCardList
          selectedIndex={selectedIndex}
          items={templates.map((template, index) => ({
            key: template.id,
            className: "template-selection-card",
            header: (
              <Flex column gap="gap.smaller">
                <Flex vAlign="center">
                  <Flex.Item grow>
                    <span>
                      <Text content={template.templateName} weight="bold" />
                    </span>
                  </Flex.Item>
                  {template.templateType === "user" && (
                    <Flex.Item push>
                      <div>
                        <TemplateManagementMenu
                          template={template}
                          onDeleted={() => loadTemplates()}
                        />
                      </div>
                    </Flex.Item>
                  )}
                </Flex>
                <Flex vAlign="center" gap="gap.smaller">
                  <PollyIcon icon="survey-question-icon" size="small" inheritColor />
                  <Text
                    content={`${template.questions.length} Question${
                      template.questions.length > 1 ? "s" : ""
                    }`}
                    weight="light"
                    size="small"
                  />
                </Flex>
              </Flex>
            ),
            body: <Text content={template.templateDescription} size="small" />,
            onClicked: () => {
              onClickTemplate(index, template);
            },
          }))}
        />
      )}
    </div>
  );
};

export const TemplateCardList = ({
  selectedIndex,
  items,
}: {
  selectedIndex: number;
  items: {
    key: string;
    className?: string;
    body?: JSX.Element;
    header?: JSX.Element;
    onClicked?: () => void;
  }[];
}) => {
  const { teamsContext } = useTeamsContext();
  const isMobile = isMobileClient(teamsContext);

  const computedItems = useMemo(() => {
    if (isMobile) {
      return items;
    }

    const ci = [...items];
    while (ci.length % 2 !== 0) {
      ci.push({ key: `empty-${ci.length}` });
    }
    return ci;
  }, [items, isMobile]);

  return (
    <Flex className="card-list-box" wrap={!isMobile} column={isMobile} space="evenly">
      {computedItems.map((item, index) => {
        const classes = ["card-list-box-item"];
        classes.push(`card-list-box-item-${isMobile ? "mobile" : "desktop"}`);
        classes.push(item.className ?? "");
        if (index === selectedIndex) {
          classes.push("card-list-box-item--selected");
        }

        return (
          <Card
            className={classes.join(" ")}
            key={item.key}
            fluid={isMobile}
            style={{
              visibility: item.key.startsWith("empty-") ? "hidden" : undefined,
              alignItems: "flex-start",
            }}
            centered
            onClick={item.onClicked}
          >
            {item.header && <CardHeader styles={{ width: "100%" }}>{item.header}</CardHeader>}
            <CardBody className="card-list-box-item-body">{item.body}</CardBody>
          </Card>
        );
      })}
    </Flex>
  );
};
